import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router';
import { RedirectIfAuthed, RequireAuth, RequireShopifySession } from '../lib/auth';
import { EMBEDDED_BASE } from '../lib/embedded';
import { PageLoader } from '../ui/Spinner';
import { AppShell } from './AppShell';
import { RouteError } from './RouteError';
import {
  ForgotPasswordPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from '../features/auth/AuthPages';

// Feature screens are split into their own chunks; the public tracking page in particular
// must stay small because every end customer loads it.
const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage'));
const OrdersPage = lazy(() => import('../features/orders/OrdersPage'));
const OrderDetailPage = lazy(() => import('../features/orders/OrderDetailPage'));
const ShipmentsPage = lazy(() => import('../features/shipments/ShipmentsPage'));
const ShipmentDetailPage = lazy(() => import('../features/shipments/ShipmentDetailPage'));
const AnalyticsPage = lazy(() => import('../features/analytics/AnalyticsPage'));
const TrackingPageSettings = lazy(() => import('../features/tracking-page/TrackingPageSettings'));
const IntegrationsPage = lazy(() => import('../features/integrations/IntegrationsPage'));
const BillingPage = lazy(() => import('../features/billing/BillingPage'));
const MockCheckoutPage = lazy(() => import('../features/billing/MockCheckoutPage'));
const SettingsPage = lazy(() =>
  import('../features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const PublicTrackPage = lazy(() => import('../features/public-tracking/PublicTrackPage'));
const AdminRoutes = lazy(() => import('../features/admin/AdminRoutes'));

const page = (node: ReactNode) => <Suspense fallback={<PageLoader />}>{node}</Suspense>;

/** The merchant screens — identical in both modes. */
const merchantScreens: RouteObject[] = [
  { path: '/dashboard', element: page(<DashboardPage />) },
  { path: '/orders', element: page(<OrdersPage />) },
  { path: '/orders/:id', element: page(<OrderDetailPage />) },
  { path: '/shipments', element: page(<ShipmentsPage />) },
  { path: '/shipments/:id', element: page(<ShipmentDetailPage />) },
  { path: '/analytics', element: page(<AnalyticsPage />) },
  { path: '/tracking-page', element: page(<TrackingPageSettings />) },
  { path: '/integrations', element: page(<IntegrationsPage />) },
  { path: '/billing', element: page(<BillingPage />) },
  { path: '/billing/mock-checkout', element: page(<MockCheckoutPage />) },
  { path: '/settings', element: page(<SettingsPage />) },
];

/** TrackFlow's own site: sign-in pages, merchant app, admin and customer tracking pages. */
export function createStandaloneRouter() {
  return createBrowserRouter([
    {
      errorElement: <RouteError />,
      children: [
        { path: '/', element: <Navigate to="/dashboard" replace /> },
        {
          element: (
            <RedirectIfAuthed>
              <Outlet />
            </RedirectIfAuthed>
          ),
          children: [
            { path: '/login', element: <LoginPage /> },
            { path: '/register', element: <RegisterPage /> },
            { path: '/forgot-password', element: <ForgotPasswordPage /> },
          ],
        },
        { path: '/reset-password', element: <ResetPasswordPage /> },
        { path: '/verify-email', element: <VerifyEmailPage /> },

        // Public, unauthenticated: customers' tracking pages.
        { path: '/track', element: page(<PublicTrackPage />) },
        { path: '/t/:slug', element: page(<PublicTrackPage />) },

        {
          element: (
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          ),
          children: [
            ...merchantScreens,
            { path: '/admin/*', element: <RequireAuth admin>{page(<AdminRoutes />)}</RequireAuth> },
          ],
        },
        { path: '*', element: <RouteError notFound /> },
      ],
    },
  ]);
}

/**
 * Inside Shopify admin (App URL = <origin>/shopify). Identity comes from App Bridge ID tokens,
 * so there are no sign-in pages; Shopify's own navigation replaces TrackFlow's sidebar.
 */
export function createEmbeddedRouter() {
  return createBrowserRouter(
    [
      {
        errorElement: <RouteError embedded />,
        children: [
          {
            element: (
              <RequireShopifySession>
                <AppShell embedded />
              </RequireShopifySession>
            ),
            children: [
              { index: true, element: <Navigate to="/dashboard" replace /> },
              ...merchantScreens,
            ],
          },
          { path: '*', element: <RouteError notFound embedded /> },
        ],
      },
    ],
    { basename: EMBEDDED_BASE },
  );
}
